import React, { useState } from "react";
import commonStyles from "repoV2/features/CheckoutPages/modules/CheckoutAccordion/CheckoutAccordion.module.scss";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import ExlyInput from "repoV2/features/Common/modules/Form/modules/ExlyInput/ExlyInput";
import InputAdornment from "@mui/material/InputAdornment";
import { Button } from "repoV2/components/common/Button";
import Skeleton from "@mui/material/Skeleton";
import { classNames } from "repoV2/utils/common/render/classNames";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import CheckoutFormGroupHeader from "repoV2/features/CheckoutPages/modules/CheckoutFormGroupHeader/CheckoutFormGroupHeader";
import checkoutCommonStyles from "repoV2/features/CheckoutPages/CheckoutCommonStyles.module.scss";
import useGetLoyaltyAndPromoDetails from "repoV2/features/CheckoutPages/utils/useGetLoyaltyAandPromotion";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { coupon_code_input_field_name } from "./CouponCodeFormGroup.constants";
import styles from "./CouponCodeFormGroup.module.scss";
import CheckoutAccordion from "../../../../../CheckoutAccordion/CheckoutAccordion";
import { IHelperTextState } from "./CouponCodeFormGroup.interfaces";
import { HelperTextComponent } from "./modules/HelperTextComponent/HelperTextComponent";
import { useCouponCodeFormGroup } from "./CouponCodeFormGroup.utils";
import LoyaltyCoupons from "./modules/LoyaltyCoupons/LoyaltyCoupons";
import PromotionOffer from "./modules/PromotionOffer/PromotionOffer";

const getHelperTextClassName = ({ type }: IHelperTextState) =>
    classNames(styles.helperText, styles[`helperText-${type}`]);

const getTextFieldClassName = ({ type }: IHelperTextState) =>
    classNames(styles.textFieldRoot, styles[`textFieldRoot-${type}`]);

const CouponCodeFormGroup = () => {
    const {
        onCouponCodeFieldChange,
        helperTextDetails,
        isCouponCodeApplied,
        showProcessingState,
        onRemoveCouponCodeClick,
        showRemoveCouponCodeState,
        showApplyCouponCodeState,
        onApplyCouponCodeClick,
        isMultipleUsersExist,
        currency,
    } = useCouponCodeFormGroup();
    const { listingUuid, hostName } = useCheckoutBasicDetailsContext();

    const [isLoyaltyCouponModalOpen, setIsLoyaltyCouponModalOpen] =
        useState<boolean>(false);

    const { showLoyalty, promotionalCoupons = [] } =
        useGetLoyaltyAndPromoDetails({
            hostName,
            listingUuid,
        });

    const onRemoveClickHandler = () => {
        onRemoveCouponCodeClick();
    };

    const inputAdornmentJSX = (
        <InputAdornment position="end">
            {showProcessingState ? (
                <div className={styles.processingCouponCode}>
                    <Skeleton variant="circular" width={10} height={10} />
                    <Skeleton variant="circular" width={10} height={10} />
                    <Skeleton variant="circular" width={10} height={10} />
                </div>
            ) : null}
            {showRemoveCouponCodeState ? (
                <Button
                    className={classNames(
                        styles.buttonBase,
                        styles.removeButton
                    )}
                    type="button"
                    design="plain"
                    onClick={onRemoveClickHandler}
                >
                    REMOVE
                </Button>
            ) : null}
            {showApplyCouponCodeState ? (
                <Button
                    className={classNames(
                        styles.buttonBase,
                        styles.applyButton
                    )}
                    type="button"
                    design="plain"
                    onClick={() => {
                        onApplyCouponCodeClick({});
                    }}
                >
                    APPLY
                </Button>
            ) : null}
        </InputAdornment>
    );

    return (
        <CheckoutAccordion
            defaultExpanded
            title={
                <div className={styles.title}>
                    <div>
                        <CheckoutFormGroupHeader
                            title="Apply Coupon"
                            iconJSX={
                                <LocalActivityOutlinedIcon
                                    className={styles.headerIconStyle}
                                />
                            }
                        />
                    </div>
                    {!isMultipleUsersExist && showLoyalty && (
                        <div className={styles.loyalty}>
                            <div
                                className={styles.loyaltyText}
                                onClick={() =>
                                    setIsLoyaltyCouponModalOpen(true)
                                }
                            >
                                Loyalty Coupons
                            </div>
                            <KeyboardArrowRightRoundedIcon
                                className={styles.rightIconStyle}
                            />
                        </div>
                    )}
                </div>
            }
            className={commonStyles.Wrapper}
            expanded
            classes={{
                root: commonStyles.formGroupRoot,
                disabled: commonStyles.formGroupDisabled,
            }}
            headerClasses={{
                root: classNames(commonStyles.headerRoot, styles.cursorStyle),
                content: commonStyles.headerContent,
                disabled: commonStyles.formGroupDisabled,
                expanded: commonStyles.formGroupExpanded,
            }}
            unmountOnExit
            showExpandIcon={false}
        >
            <div className={commonStyles.BasicForm}>
                <ExlyInput
                    name={coupon_code_input_field_name}
                    onChange={onCouponCodeFieldChange}
                    placeholder="Enter Coupon Code"
                    fullWidth
                    classes={{
                        helperText: getHelperTextClassName(helperTextDetails),
                    }}
                    helperText={<HelperTextComponent {...helperTextDetails} />}
                    disabled={isCouponCodeApplied}
                    InputProps={{
                        className: checkoutCommonStyles.inputBaseText,
                        classes: { input: styles.htmlInputTag },
                        endAdornment: inputAdornmentJSX,
                    }}
                    overrideInputProps={{
                        className: classNames(
                            checkoutCommonStyles.inputCustomStyles,
                            getTextFieldClassName(helperTextDetails)
                        ),
                    }}
                />
                {!isMultipleUsersExist && showLoyalty && (
                    <LoyaltyCoupons
                        open={isLoyaltyCouponModalOpen}
                        onModalClose={() => setIsLoyaltyCouponModalOpen(false)}
                        setIsModalOpen={setIsLoyaltyCouponModalOpen}
                        onApplyCouponCodeClick={onApplyCouponCodeClick}
                    />
                )}
                {!checkIsArrayEmpty(promotionalCoupons) && (
                    <PromotionOffer
                        promotionalCoupons={promotionalCoupons}
                        currency={currency}
                        onApplyCouponCodeClick={onApplyCouponCodeClick}
                    />
                )}
            </div>
        </CheckoutAccordion>
    );
};

export default CouponCodeFormGroup;
